class SectionId {
  static #separator = '__';

  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function toggleProductSubmitButton(productForm, disable = true, text) {
  if (!productForm) return;

  if (typeof productForm.toggleSubmitButton === 'function') {
    productForm.toggleSubmitButton(disable, text);
    return;
  }

  const submitButton = productForm.querySelector('[type="submit"]');
  if (!submitButton) return;

  const submitButtonText =
    submitButton.querySelector('.main-atc__label__text') || submitButton.querySelector('span');

  if (disable) {
    submitButton.setAttribute('disabled', 'disabled');
    if (text && submitButtonText) submitButtonText.textContent = text;
  } else {
    submitButton.removeAttribute('disabled');
    if (submitButtonText) submitButtonText.textContent = window.variantStrings.addToCart;
  }
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initStickyCartObserver();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        toggleProductSubmitButton(productForm, true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;

        const selectedVariantScript = productInfoNode.querySelector('variant-selects [data-selected-variant]');
        if (selectedVariantScript?.textContent?.trim()) {
          return JSON.parse(selectedVariantScript.textContent);
        }

        const variantSelects = productInfoNode.querySelector('variant-selects');
        const variantsScript = variantSelects?.querySelector('script[type="application/json"]');
        if (!variantsScript?.textContent?.trim()) return null;

        const variants = JSON.parse(variantsScript.textContent);
        if (!Array.isArray(variants) || variants.length === 0) return null;
        if (variants.length === 1) return variants[0];

        const selectedValues = Array.from(
          variantSelects.querySelectorAll('fieldset input:checked, select option:checked')
        ).map((element) => element.value);

        return (
          variants.find((variant) =>
            selectedValues.every((value, index) => variant[`option${index + 1}`] === value)
          ) || null
        );
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          toggleProductSubmitButton(
            this.productForm,
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.updateStickyCart(html);
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      initStickyCartObserver() {
        // Always reset observer (DOM is replaced on variant change)
        this.stickyCartObserver?.disconnect();

        const buttons = this.querySelector('.product-form__buttons');
        const stickyCart = this.querySelector('.product__sticky-cart');

        if (!buttons || !stickyCart) return;

        this.stickyCartObserver = new IntersectionObserver(
          ([entry]) => {
            const rect = entry.boundingClientRect;

            if (entry.isIntersecting) {
              stickyCart.classList.add('is-hidden');
            } else if (rect.top < 0) {
              stickyCart.classList.remove('is-hidden');
            }
          },
          { threshold: 0 }
        );

        this.stickyCartObserver.observe(buttons);
      }

      setUnavailable() {
        toggleProductSubmitButton(this.productForm, true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules','buy-button-product-shipping-cut']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateStickyCart(html) {
        const productStickyCart = html.querySelector('.product__sticky-cart');
        if (productStickyCart) {
          HTMLUpdateUtility.viewTransition(this.productStickyCart, productStickyCart, this.preProcessHtmlCallbacks);
        }

        this.initStickyCartObserver();
      }

      updateMedia(html, variantFeaturedMediaId) {
        // if (!variantFeaturedMediaId) return;

        const productGallerySlider = html.querySelector('product-gallery-slider');
        if (productGallerySlider && this.productGallerySlider) {
          HTMLUpdateUtility.viewTransition(this.productGallerySlider, productGallerySlider, this.preProcessHtmlCallbacks);
          return;
        }

        const mediaGalleryNew = html.getElementById(`MediaGalleryMain-${this.sectionId}`);
        const mediaGalleryCurrent = document.getElementById(`MediaGalleryMain-${this.dataset.section}`);
        if (mediaGalleryNew && mediaGalleryCurrent) {
          mediaGalleryCurrent.innerHTML = mediaGalleryNew.innerHTML;
        }

        // const mediaGallerySource = this.querySelector('media-gallery ul');
        // const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        // const refreshSourceData = () => {
        //   if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
        //   const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
        //   const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
        //   const sourceMap = new Map(
        //     mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
        //   );
        //   return [mediaGallerySourceItems, sourceSet, sourceMap];
        // };

        // if (mediaGallerySource && mediaGalleryDestination) {
        //   let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        //   const mediaGalleryDestinationItems = Array.from(
        //     mediaGalleryDestination.querySelectorAll('li[data-media-id]')
        //   );
        //   const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
        //   let shouldRefresh = false;

        //   // add items from new data not present in DOM
        //   for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
        //     if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
        //       mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
        //       shouldRefresh = true;
        //     }
        //   }

        //   // remove items from DOM not present in new data
        //   for (let i = 0; i < mediaGallerySourceItems.length; i++) {
        //     if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
        //       mediaGallerySourceItems[i].remove();
        //       shouldRefresh = true;
        //     }
        //   }

        //   // refresh
        //   if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

        //   // if media galleries don't match, sort to match new data order
        //   mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
        //     const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

        //     if (sourceData && sourceData.index !== destinationIndex) {
        //       mediaGallerySource.insertBefore(
        //         sourceData.item,
        //         mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
        //       );

        //       // refresh source now that it has been modified
        //       [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        //     }
        //   });
        // }

        // // set featured media as active in the media gallery
        // this.querySelector(`media-gallery`)?.setActiveMedia?.(
        //   `${this.dataset.section}-${variantFeaturedMediaId}`,
        //   true
        // );

        // // update media modal
        // const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        // const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        // if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productStickyCart() {
        return this.querySelector(`.product__sticky-cart`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productGallerySlider() {
        return this.querySelector('product-gallery-slider');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


class ProductGallerySlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSliders();
    this.initCustomerTrigger();
  }

  initCustomerTrigger() {
    const triggerBtn = this.querySelector("#image__customer_click");

    const targetSection = document.querySelector("#product-customer-view");

    if (!triggerBtn) return;

    if (targetSection) {
      triggerBtn.addEventListener("click", () => {
        targetSection.scrollIntoView({ behavior: "smooth" });
      });
    } else {
      triggerBtn.style.display = "none";
    }
  }

  initSliders() {
    const mainEl = this.querySelector('[main-slider-element]');
    const thumbEl = this.querySelector('[thumbnail-slider-element]');

    if (!mainEl || !thumbEl) return;

    const thumbSlides = thumbEl.querySelectorAll('.swiper-slide').length;

    // Decide slidesPerView
    const slidesPerViewValue = thumbSlides < 5 ? 'auto' : 5;

    // Thumbnail slider (vertical, NO arrows)
    const thumbSwiper = new Swiper(thumbEl, {
      direction: 'vertical',
      slidesPerView: slidesPerViewValue,
      spaceBetween: 10,
      freeMode: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      mousewheel: true,
      observer: true,
      observeParents: true,
      navigation: {
        nextEl: this.querySelector('[next-thumb-slider]'),
        prevEl: this.querySelector('[prev-thumb-slider]'),
      },
    });

    // thumbSwiper.on('slideChange', () => {
    //   mainSwiper.slideTo(thumbSwiper.activeIndex);
    // });

    // Main slider (with arrows)
    const mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      autoHeight: true,
      fadeEffect: { crossFade: true },
      navigation: {
        nextEl: mainEl.querySelector('[next-main-slider]'),
        prevEl: mainEl.querySelector('[prev-main-slider]'),
      },
      thumbs: {
        swiper: thumbSwiper,
      },
      on: {
        slideChangeTransitionStart() {
          // Pause all videos
          mainEl.querySelectorAll('video').forEach(video => {
            video.pause();
          });
        },
        slideChangeTransitionEnd() {
          // Play video in active slide
          const activeSlide = mainEl.querySelector('.swiper-slide-active');
          const video = activeSlide?.querySelector('video');
          if (video) video.play();
        }
      }
    });
  }

  initCustomerTrigger() {
    const triggerBtn = this.querySelector("#image__customer_click");
    const targetSection = document.querySelector("#product-customer-view");

    if (!triggerBtn) return;

    if (targetSection) {
      triggerBtn.addEventListener("click", () => {
        targetSection.scrollIntoView({ behavior: "smooth" });
      });
    } else {
      triggerBtn.style.display = "none";
    }
  }
}
customElements.define("product-gallery-slider", ProductGallerySlider);

class ShippingDetails extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
  }

  connectedCallback() {
    this.cutoff = this.dataset.cutOff || '13:00'; // 1 PM EST

    this.hoursText = this.dataset.hours || 'hours';
    this.minutesText = this.dataset.minutes || 'minutes';
    this.secondsText = this.dataset.seconds || 'seconds';

    this.timeEl = this.querySelector('#shipping__time');
    if (!this.timeEl) return;

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.update();
    this.interval = setInterval(() => this.update(), 1000);
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  // ✅ Accurate EST/EDT time
  getESTNow() {
    const now = new Date();
    const estTime = new Date(
      now.toLocaleString('en-US', { timeZone: 'America/Toronto' })
    );
    return estTime;
  }

  formatCountdown(hours, minutes, seconds) {
    if (hours > 0) {
      return `${hours} ${this.hoursText} ${minutes} ${this.minutesText}`;
    }

    if (minutes > 0) {
      return `${minutes} ${this.minutesText} ${seconds} ${this.secondsText}`;
    }

    return `${seconds} ${this.secondsText}`;
  }

  update() {
    const [cutoffHour, cutoffMinute] = this.cutoff.split(':').map(Number);
    const now = this.getESTNow();

    // Cutoff is ALWAYS today at 1 PM EST
    const cutoffToday = new Date(now);
    cutoffToday.setHours(cutoffHour, cutoffMinute, 0, 0);

    // ❌ After 1 PM EST → hide widget permanently
    if (now >= cutoffToday) {
      this.style.display = 'none';
      this.stop(); // 🔥 stop timer
      return;
    }

    // ✅ Before 1 PM EST → show countdown
    const diffMs = cutoffToday - now;
    const totalSeconds = Math.floor(diffMs / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.timeEl.textContent = this.formatCountdown(hours, minutes, seconds);
    this.style.display = '';
  }
}

if (!customElements.get('shipping-details')) {
  customElements.define('shipping-details', ShippingDetails);
}

// Product description toggle
const desc = document.querySelector(".product-description"), btn = document.querySelector(".read-more-btn");
btn?.addEventListener("click", () => {
  desc.style.maxHeight = desc.classList.toggle("expanded") ? desc.scrollHeight + "px" : "38px";
});

// FAQ accordion
document.querySelectorAll('.faq-item').forEach(item => {
  const q = item.querySelector('.faq-question'), a = item.querySelector('.faq-answer');
  q.addEventListener('click', () => {
    const open = a.style.maxHeight;
    document.querySelectorAll('.faq-answer').forEach(ans => { ans.style.maxHeight = null; ans.closest('.faq-item').classList.remove('active'); });
    if (!open) { a.style.maxHeight = a.scrollHeight + 'px'; item.classList.add('active'); }
  });
});

document.addEventListener("DOMContentLoaded", function () {
  const buttons = document.querySelectorAll(".product__tab_btn");
  const contents = document.querySelectorAll(".product__tab_content");

  buttons.forEach(button => {
    button.addEventListener("click", function () {
      const target = this.getAttribute("data-tab");

      // Remove active
      buttons.forEach(btn => btn.classList.remove("active"));
      contents.forEach(content => content.classList.remove("active"));

      // Add active
      this.classList.add("active");
      document.getElementById("tab-" + target).classList.add("active");
    });
  });
});
